import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Monitor, LogOut, Trash2 } from "lucide-react";
import AdminNotices from "@/components/AdminNotices";
import AdminVendors from "@/components/AdminVendors";
import { API, adminCall } from "@/utils/api";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const inputClass =
  "w-full bg-night border border-line px-4 py-3 text-sm text-ink focus:border-brand outline-none font-mono transition-colors";
const labelClass = "block font-mono text-xs uppercase text-mute mb-2";

const AdminPage = () => {
  const [user, setUser] = useState(undefined); // undefined = checking, null = logged out
  const [enquiries, setEnquiries] = useState([]);
  const [notices, setNotices] = useState([]);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const loadEnquiries = useCallback(async () => {
    try {
      const res = await adminCall(() => axios.get(`${API}/admin/enquiries`, { withCredentials: true }));
      setEnquiries(res.data);
    } catch {
      setUser(null);
    }
  }, []);

  const loadNotices = useCallback(async () => {
    try {
      const res = await adminCall(() => axios.get(`${API}/admin/notices`, { withCredentials: true }));
      setNotices(res.data);
    } catch (err) {
      console.error("Failed to load notices", err);
    }
  }, []);

  const updateStatus = useCallback(async (id, status) => {
    try {
      const res = await adminCall(() =>
        axios.patch(
          `${API}/admin/enquiries/${id}/status`,
          { status },
          { withCredentials: true }
        )
      );
      setEnquiries((prev) => prev.map((e) => (e.id === id ? res.data : e)));
    } catch {
      setUser(null);
    }
  }, []);

  const deleteEnquiry = useCallback(async (id) => {
    if (!window.confirm("Delete this application permanently?")) return;
    try {
      await adminCall(() => axios.delete(`${API}/admin/enquiries/${id}`, { withCredentials: true }));
      setEnquiries((prev) => prev.filter((e) => e.id !== id));
    } catch {
      alert("Could not delete this application. Please try again.");
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser(res.data);
      } catch {
        try {
          await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
          const res = await axios.get(`${API}/auth/me`, { withCredentials: true });
          setUser(res.data);
        } catch {
          setUser(null);
        }
      }
    })();
  }, []);

  useEffect(() => {
    if (user) {
      loadEnquiries();
      loadNotices();
    }
  }, [user, loadEnquiries, loadNotices]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(
        `${API}/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      setUser(data);
    } catch (err) {
      setError(
        err.response
          ? formatApiErrorDetail(err.response?.data?.detail) || err.message
          : "Cannot reach the server. Please check your connection and try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } finally {
      setUser(null);
      setEnquiries([]);
    }
  };

  return (
    <div className="bg-night text-ink font-sans antialiased min-h-screen selection:bg-brand selection:text-night">
      <header className="bg-surface border-b border-line">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <a href="/" className="flex items-center gap-3" data-testid="admin-brand-link">
            <Monitor className="w-6 h-6 text-brand" strokeWidth={2} />
            <span className="font-mono text-sm tracking-wider uppercase font-bold text-brand">
              DSS Tender Hub — Admin
            </span>
          </a>
          {user && (
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 border border-line hover:border-brand text-mute hover:text-brand font-mono text-xs uppercase tracking-wider px-4 py-2 transition-colors"
              data-testid="admin-logout-btn"
            >
              <LogOut className="w-4 h-4" /> Logout
            </button>
          )}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-16">
        {user === undefined && (
          <p className="font-mono text-xs uppercase text-mute tracking-widest" data-testid="admin-loading">
            Checking session...
          </p>
        )}

        {user === null && (
          <div className="max-w-md mx-auto">
            <p className="font-mono text-xs uppercase text-brand tracking-widest mb-2">Restricted Access</p>
            <h1 className="font-display text-4xl uppercase mb-8 tracking-wide">Admin Login</h1>
            <form onSubmit={handleLogin} className="bg-surface border border-line p-8 space-y-6" data-testid="admin-login-form">
              <div>
                <label className={labelClass} htmlFor="admin-email">Email</label>
                <input
                  type="email"
                  id="admin-email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={inputClass}
                  placeholder="admin email"
                  data-testid="admin-email-input"
                />
              </div>
              <div>
                <label className={labelClass} htmlFor="admin-password">Password</label>
                <input
                  type="password"
                  id="admin-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={inputClass}
                  placeholder="password"
                  data-testid="admin-password-input"
                />
              </div>
              {error && (
                <p className="font-mono text-xs text-warn" data-testid="admin-login-error">{error}</p>
              )}
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-brand hover:bg-brandhover disabled:opacity-60 text-night font-mono text-xs font-bold uppercase tracking-widest py-4 transition-colors"
                data-testid="admin-login-submit-btn"
              >
                {loading ? "Verifying..." : "Sign In →"}
              </button>
            </form>
          </div>
        )}

        {user && (
          <div data-testid="admin-dashboard">
            <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 pb-6 border-b border-line">
              <div>
                <p className="font-mono text-xs uppercase text-brand tracking-widest mb-2">Enquiry Register</p>
                <h1 className="font-display text-[clamp(28px,3.5vw,48px)] uppercase tracking-wide">
                  Tender Enquiries
                </h1>
              </div>
              <p className="text-sm font-mono text-mute" data-testid="enquiry-count">
                {enquiries.length} record{enquiries.length === 1 ? "" : "s"}
              </p>
            </div>

            {enquiries.length === 0 ? (
              <div className="border border-line p-8 bg-surface text-center" data-testid="enquiries-empty">
                <p className="text-sm font-mono text-mute uppercase tracking-wider">
                  No enquiries yet. New submissions from the website form will appear here.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto border border-line" data-testid="enquiries-table-wrap">
                <table className="w-full text-sm" data-testid="enquiries-table">
                  <thead>
                    <tr className="bg-surface font-mono text-xs uppercase text-mute text-left">
                      <th className="px-4 py-3 border-b border-line">Date</th>
                      <th className="px-4 py-3 border-b border-line">Organisation</th>
                      <th className="px-4 py-3 border-b border-line">Stakeholder</th>
                      <th className="px-4 py-3 border-b border-line">Contact</th>
                      <th className="px-4 py-3 border-b border-line">Email</th>
                      <th className="px-4 py-3 border-b border-line">Region</th>
                      <th className="px-4 py-3 border-b border-line">Scope</th>
                      <th className="px-4 py-3 border-b border-line">Status</th>
                      <th className="px-4 py-3 border-b border-line"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {enquiries.map((enq) => (
                      <tr key={enq.id} className="border-b border-line hover:bg-surface transition-colors" data-testid={`enquiry-row-${enq.id}`}>
                        <td className="px-4 py-3 font-mono text-xs text-mute whitespace-nowrap">
                          {new Date(enq.created_at).toLocaleString("en-IN")}
                        </td>
                        <td className="px-4 py-3 font-bold">{enq.entity_name}</td>
                        <td className="px-4 py-3 text-mute">{enq.stakeholder_type}</td>
                        <td className="px-4 py-3 text-brand font-mono text-xs">{enq.contact}</td>
                        <td className="px-4 py-3 text-brand font-mono text-xs">{enq.email || "—"}</td>
                        <td className="px-4 py-3 text-mute">{enq.region}</td>
                        <td className="px-4 py-3 text-mute max-w-xs">{enq.scope || "—"}</td>
                        <td className="px-4 py-3">
                          <select
                            value={enq.status || "new"}
                            onChange={(e) => updateStatus(enq.id, e.target.value)}
                            className={`bg-night border border-line px-2 py-1 text-xs font-mono uppercase outline-none focus:border-brand transition-colors ${
                              (enq.status || "new") === "new"
                                ? "text-brand"
                                : enq.status === "contacted"
                                  ? "text-warn"
                                  : "text-ok"
                            }`}
                            data-testid={`enquiry-status-${enq.id}`}
                          >
                            <option value="new">New</option>
                            <option value="contacted">Contacted</option>
                            <option value="closed">Closed</option>
                          </select>
                        </td>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => deleteEnquiry(enq.id)}
                            className="text-mute hover:text-warn transition-colors"
                            title="Delete application"
                            data-testid={`enquiry-delete-${enq.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <AdminNotices notices={notices} onChanged={loadNotices} />
            <AdminVendors notices={notices} />
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminPage;
